// src/pages/AccessRemoval.jsx
// Access Removal Request — Creates a SIM-T ticket

import React, { useState, useEffect } from 'react';

const AccessRemoval = ({ userAlias }) => {
    const [title, setTitle] = useState('Access Removal Request');
    const [permissionUrls, setPermissionUrls] = useState('');
    const [usersToRemove, setUsersToRemove] = useState('');
    const [reason, setReason] = useState('');
    const [approvedBy, setApprovedBy] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [submitError, setSubmitError] = useState('');

    const isValid = title.trim() && permissionUrls.trim();

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const { getAccessToken, refreshToken } = await import('../services/auth');
                const token = getAccessToken();
                if (!token && refreshToken) await refreshToken();
            } catch (e) {}
        };
        checkAuth();
    }, []);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const { getAccessToken } = await import('../services/auth');
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
            const token = getAccessToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const descBody = [
                `## Access Removal Request`,
                ``,
                `**Requester:** ${userAlias}`,
                `**Approved By:** ${approvedBy || 'N/A'}`,
                ``,
                `---`,
                `### Permission(s) to be removed`,
                permissionUrls,
                ``,
                `### Users to remove`,
                usersToRemove || 'N/A',
                ``,
                `### Reason for removal`,
                reason || 'N/A',
            ].join('\n');

            const response = await fetch(`${API_BASE_URL}/validate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    operation: 'createTicket',
                    title: `[Access Removal] ${title}`,
                    description: descBody,
                    severity: 'SEV_5',
                    requesterAlias: userAlias
                })
            });

            const raw = await response.json();
            const result = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;

            if (response.status === 401) { sessionStorage.clear(); window.location.href = '/'; return; }
            if (!response.ok || result.error) throw new Error(result.error || `API error: ${response.status}`);

            setSubmitSuccess(result);
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitSuccess) {
        return (
            <div style={{ maxWidth: '700px', margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h2 style={{ color: '#166534', marginBottom: '8px' }}>Access Removal Request Submitted!</h2>
                <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>Ticket ID: <strong>{submitSuccess.ticketId}</strong></p>
                {submitSuccess.ticketUrl && (
                    <a href={submitSuccess.ticketUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-block', padding: '10px 20px', borderRadius: '8px', background: '#0073bb', color: '#fff', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                        View Ticket on SIM-T ↗
                    </a>
                )}
                <div style={{ marginTop: '20px' }}>
                    <button onClick={() => window.location.href = '/'} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>← Back to Home</button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 24px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '4px' }}>🚫 Access Removal</h1>

            {/* Info Section */}
            <div style={{ padding: '16px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', marginBottom: '24px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 10px' }}>Access removal is the process of terminating or revoking a user's ability to access specific tools, widgets, or resources within Paragon Tenants. This security measure is typically performed when:</p>
                <ol style={{ margin: '0 0 10px', paddingLeft: '20px' }}>
                    <li>An employee leaves the organization</li>
                    <li>Changes roles within the company</li>
                    <li>No longer requires access to specific systems</li>
                    <li>As part of regular access review and cleanup</li>
                </ol>
                <p style={{ margin: '0 0 10px' }}>Use this SIM template to remove access for users from a SWAT P&S managed Paragon tenant. Please provide complete user details to ensure timely removal of access. All requests will be handled according to standard deprovisioning procedures and security protocols.</p>
                <p style={{ margin: '0 0 4px', fontWeight: '700', color: '#991b1b' }}>⚠️ Please ensure these removal requests have been properly communicated/approved as P&S will not be accountable for any SLA impact caused by these changes.</p>
                <p style={{ margin: '0', fontSize: '12px', color: '#991b1b' }}>⭐ <strong>Note: The first response SLA for this SIM is 1 business day.</strong></p>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>If you don't hear from us within the SLA please drop an email to: ctps-permissions-security@amazon.com</p>
            </div>

            {submitError && <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '13px', color: '#991b1b', marginBottom: '16px' }}>❌ {submitError}</div>}

            {/* Title */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Title <span style={{ color: '#d13212' }}>*</span></label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* Permission URLs */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Permission(s) to be removed <span style={{ color: '#d13212' }}>*</span> <span style={{ fontWeight: '400', color: '#666' }}>(Insert permission URL)</span></label>
                <textarea value={permissionUrls} onChange={(e) => setPermissionUrls(e.target.value)} rows={3} placeholder="e.g., https://permissions.amazon.com/a/team/Paragon%20Investigations%20-%20Base%20PI"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Users to remove */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>User(s) to remove (comma separated, without @amazon):</label>
                <textarea value={usersToRemove} onChange={(e) => setUsersToRemove(e.target.value)} rows={2} placeholder="e.g., johndoe, janedoe"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Reason */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Reason for removal:</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g., Employee left the org, role change, access review cleanup"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Approved by */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Approved/communicated by (manager alias):</label>
                <input type="text" value={approvedBy} onChange={(e) => setApprovedBy(e.target.value)} placeholder="e.g., managername"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* Submit */}
            <button onClick={handleSubmit} disabled={!isValid || isSubmitting}
                style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: isValid && !isSubmitting ? 'pointer' : 'not-allowed', border: 'none', background: isValid ? '#dc2626' : '#ccc', color: '#fff' }}>
                {isSubmitting ? '⏳ Submitting...' : '🚀 Submit Access Removal Request'}
            </button>
        </div>
    );
};

export default AccessRemoval;
